import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card, SkeletonRows, EmptyState } from "@/components/dash/ui";
import { VerificationBadge, ApprovalBadge, AccountBadge, ChecklistBadge, DocumentBadge } from "@/components/dash/PartnerBadges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, ArrowLeft, CheckCircle2, XCircle, Ban, RotateCcw, MailQuestion, FileWarning, ShieldCheck, Clock, AlertTriangle } from "lucide-react";

const TABS = ["Overview", "Organization", "Representative", "Leadership", "Business", "Documents", "Verification", "Requests", "Activity"];

const fmt = (v) => (v === null || v === undefined || v === "" ? null : Array.isArray(v) ? (v.length ? v.join(", ") : null) : typeof v === "object" ? JSON.stringify(v) : String(v));
const Field = ({ label, value, pending }) => (
  <div className="py-2 border-b border-slate-50">
    <div className="text-xs uppercase tracking-wide text-slate-400 mb-0.5">{label}</div>
    {fmt(value) ? <div className="text-sm text-slate-800 break-words">{fmt(value)}</div>
      : <div className="text-sm text-slate-400 italic">{pending ? "Pending verification" : "Not provided"}</div>}
  </div>
);
const Ring = ({ pct, label, sub }) => {
  const color = pct >= 100 ? "text-emerald-500" : pct >= 60 ? "text-amber-500" : "text-rose-500";
  return (
    <div className="flex items-center gap-3">
      <div className="relative h-12 w-12 shrink-0">
        <svg viewBox="0 0 36 36" className="h-12 w-12 -rotate-90">
          <circle cx="18" cy="18" r="15" fill="none" className="stroke-slate-100" strokeWidth="4" />
          <circle cx="18" cy="18" r="15" fill="none" className={`${color} stroke-current`} strokeWidth="4" strokeDasharray={`${(pct / 100) * 94.2} 94.2`} strokeLinecap="round" />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-[11px] font-bold text-slate-700">{pct}%</span>
      </div>
      <div><div className="text-xs font-semibold text-slate-700">{label}</div><div className="text-xs text-slate-400">{sub}</div></div>
    </div>
  );
};

function RequestDialog({ open, onClose, orgId, data, kind, onDone }) {
  const ev = data?.evaluation;
  const profileOpts = kind === "info" ? [...(ev?.profile?.missing || []), ...(ev?.profile?.optional_missing || [])] : [];
  const docOpts = kind === "docs" ? [...(ev?.documents?.missing || []), ...(ev?.documents?.rejected || []), ...(ev?.documents?.expired || [])] : [];
  const [sel, setSel] = React.useState({});
  const [message, setMessage] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { if (open) { const s = {}; docOpts.forEach((d) => { s[`doc:${d.key}`] = true; }); setSel(s); setMessage(""); } }, [open]);
  const toggle = (k) => setSel((s) => ({ ...s, [k]: !s[k] }));
  const send = async () => {
    const profile_keys = Object.keys(sel).filter((k) => sel[k] && k.startsWith("prof:")).map((k) => k.slice(5));
    const document_keys = Object.keys(sel).filter((k) => sel[k] && k.startsWith("doc:")).map((k) => k.slice(4));
    if (!profile_keys.length && !document_keys.length) return toast.error("Select at least one item");
    setBusy(true);
    try {
      const url = kind === "docs" ? `/admin/onboarding/partners/${orgId}/request-documents` : `/admin/onboarding/partners/${orgId}/information-requests`;
      await api.post(url, { request_type: kind === "docs" ? "missing_documents" : "missing_information", profile_keys, document_keys, message });
      toast.success("Request sent to partner"); onClose(); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{kind === "docs" ? "Request Documents" : "Request Information"}</DialogTitle>
          <DialogDescription>Outstanding items are detected automatically from the requirement engine.</DialogDescription></DialogHeader>
        <div className="space-y-2 mt-2" data-testid="request-items">
          {profileOpts.map((p) => (
            <label key={p.key} className="flex items-center gap-2 text-sm text-slate-700 py-1">
              <Checkbox checked={!!sel[`prof:${p.key}`]} onCheckedChange={() => toggle(`prof:${p.key}`)} data-testid={`req-prof-${p.key}`} />{p.label}</label>
          ))}
          {docOpts.map((d) => (
            <label key={d.key} className="flex items-center gap-2 text-sm text-slate-700 py-1">
              <Checkbox checked={!!sel[`doc:${d.key}`]} onCheckedChange={() => toggle(`doc:${d.key}`)} data-testid={`req-doc-${d.key}`} />{d.label}</label>
          ))}
          {!profileOpts.length && !docOpts.length && <p className="text-sm text-slate-400">No outstanding items to request.</p>}
          <div><Label>Message (optional)</Label><Input value={message} onChange={(e) => setMessage(e.target.value)} className="mt-1" data-testid="req-message" /></div>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={busy} className="bg-vs-royal text-white" data-testid="req-send">Send Request</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReasonDialog({ open, onClose, title, label, onConfirm }) {
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { if (open) setReason(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>A reason is recorded in the audit trail and sent to the partner.</DialogDescription></DialogHeader>
        <div className="mt-2"><Label>{label}</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" data-testid="reason-input" /></div>
        <div className="flex justify-end gap-2 pt-3"><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || !reason.trim()} onClick={async () => { setBusy(true); try { await onConfirm(reason); } finally { setBusy(false); } }} className="bg-rose-600 text-white" data-testid="reason-confirm">Confirm</Button></div>
      </DialogContent>
    </Dialog>
  );
}

export default function PartnerDetail() {
  const { orgId } = useParams();
  const nav = useNavigate();
  const [data, setData] = React.useState(null);
  const [tab, setTab] = React.useState("Overview");
  const [reqDialog, setReqDialog] = React.useState(null);
  const [reasonAction, setReasonAction] = React.useState(null);

  const load = React.useCallback(() => {
    api.get(`/admin/onboarding/partners/${orgId}/360`).then((r) => setData(r.data)).catch((e) => { setData(false); toast.error(apiError(e.response?.data?.detail)); });
  }, [orgId]);
  React.useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const approve = () => act(() => api.post(`/admin/onboarding/partners/${orgId}/approve`), "Partner approved");
  const reactivate = () => act(() => api.post(`/admin/onboarding/partners/${orgId}/reactivate`), "Partner reactivated");
  const verifyDoc = (docId, status, reason) => act(() => api.post(`/admin/onboarding/partners/${orgId}/documents/${docId}/verify`, { verification_status: status, reason }), `Document ${status}`);

  if (data === null) return <div><SkeletonRows rows={8} /></div>;
  if (data === false) return <EmptyState icon="Building2" title="Partner not found" />;

  const { organization: org, registration: reg, sections, evaluation: ev, status_dimensions: dims, documents, checklist, action_required: ar, approval_blockers: blockers, can_approve, timeline, requests } = data;
  const co = sections.company, rep = sections.representative, lead = sections.leadership, bd = sections.business_details || {};
  const state = data.onboarding_state;
  const suspended = dims.account_status === "suspended";

  return (
    <div className="pb-6">
      <button onClick={() => nav("/app/partners")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-vs-royal mb-4" data-testid="partner-back"><ArrowLeft className="h-4 w-4" />Partners</button>

      {/* HEADER */}
      <Card className="mb-5">
        <div className="flex flex-col lg:flex-row lg:items-start gap-5">
          <div className="flex items-start gap-4 min-w-0 flex-1">
            <div className="h-14 w-14 rounded-xl bg-vs-light flex items-center justify-center shrink-0 overflow-hidden">
              {org.logo ? <img src={org.logo} alt="" className="h-full w-full object-cover" /> : <Building2 className="h-7 w-7 text-vs-royal" />}
            </div>
            <div className="min-w-0">
              <h1 className="font-display font-bold text-xl text-slate-900 truncate" data-testid="partner-name">{co.legal_name || org.name}</h1>
              {co.display_name && <div className="text-sm text-slate-500">{co.display_name}</div>}
              <div className="flex items-center gap-2 mt-1.5 text-xs text-slate-400 flex-wrap">
                <span className="capitalize font-semibold text-vs-royal">{org.type} Partner</span><span>·</span>
                <span>{org.partner_code}</span><span>·</span>
                <span>Registered {org.created_at ? new Date(org.created_at).toLocaleDateString() : "—"}</span>
              </div>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <VerificationBadge status={dims.verification_status} data-testid="partner-verification-badge" />
                <ApprovalBadge status={dims.approval_status} />
                <AccountBadge status={dims.account_status} />
              </div>
            </div>
          </div>
          <div className="flex gap-6 shrink-0">
            <Ring pct={ev.profile_completion_percentage} label="Profile" sub={`${ev.profile.completed}/${ev.profile.required_total} fields`} />
            <Ring pct={ev.document_completion_percentage} label="Documents" sub={`${ev.documents.uploaded}/${ev.documents.required_total} uploaded`} />
          </div>
        </div>
        {/* ACTION BAR */}
        <div className="flex flex-wrap gap-2 mt-5 pt-5 border-t border-slate-100" data-testid="partner-actions">
          <Button size="sm" variant="outline" onClick={() => setReqDialog("info")} data-testid="action-request-info"><MailQuestion className="h-3.5 w-3.5 mr-1" />Request Information</Button>
          <Button size="sm" variant="outline" onClick={() => setReqDialog("docs")} data-testid="action-request-docs"><FileWarning className="h-3.5 w-3.5 mr-1" />Request Documents</Button>
          {!suspended && state !== "ACTIVE" && state !== "VERIFIED" && (
            <Button size="sm" className="bg-vs-royal text-white disabled:opacity-50" disabled={!can_approve} onClick={approve} data-testid="action-approve"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Approve Partner</Button>
          )}
          {!suspended && state !== "REJECTED" && (
            <Button size="sm" variant="outline" className="text-rose-600 border-rose-200" onClick={() => setReasonAction("reject")} data-testid="action-reject"><XCircle className="h-3.5 w-3.5 mr-1" />Reject</Button>
          )}
          {!suspended ? (
            <Button size="sm" variant="outline" className="text-amber-600 border-amber-200" onClick={() => setReasonAction("suspend")} data-testid="action-suspend"><Ban className="h-3.5 w-3.5 mr-1" />Suspend</Button>
          ) : (
            <Button size="sm" className="bg-emerald-600 text-white" onClick={reactivate} data-testid="action-reactivate"><RotateCcw className="h-3.5 w-3.5 mr-1" />Reactivate</Button>
          )}
        </div>
        {!can_approve && blockers.length > 0 && (
          <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3" data-testid="approval-blockers">
            <div className="flex items-center gap-1.5 text-sm font-semibold text-amber-800 mb-1"><AlertTriangle className="h-4 w-4" />Partner cannot be approved yet</div>
            <ul className="text-xs text-amber-700 list-disc ml-5">{blockers.map((b, i) => <li key={i}>{b}</li>)}</ul>
          </div>
        )}
      </Card>

      {/* TABS */}
      <div className="flex items-center gap-1 mb-5 overflow-x-auto border-b border-slate-100" data-testid="partner-tabs">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.toLowerCase()}`}
            className={`whitespace-nowrap px-3.5 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t ? "border-vs-royal text-vs-royal" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{t}</button>
        ))}
      </div>

      {tab === "Overview" && (
        <div className="space-y-5">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[["Profile", `${ev.profile_completion_percentage}%`, `${ev.profile.completed}/${ev.profile.required_total} required`],
              ["Documents Uploaded", `${ev.documents.uploaded}/${ev.documents.required_total}`, "submission"],
              ["Documents Verified", `${ev.documents.verified}/${ev.documents.verification_required_total}`, "verification"],
              ["Outstanding", Object.values(ar).reduce((a, b) => a + b, 0), "action items"]].map(([l, v, s]) => (
              <Card key={l} className="!p-4"><div className="text-2xl font-bold text-slate-800">{v}</div><div className="text-xs text-slate-500 mt-0.5">{l}</div><div className="text-[11px] text-slate-400">{s}</div></Card>
            ))}
          </div>
          <Card title="Action Required">
            {Object.values(ar).reduce((a, b) => a + b, 0) === 0 ? <p className="text-sm text-slate-400">No outstanding verification actions.</p> : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="action-required">
                {[["Missing Information", ar.missing_information], ["Missing Documents", ar.missing_documents], ["Rejected Documents", ar.rejected_documents], ["Pending Verification", ar.pending_verification], ["Expired Documents", ar.expired_documents]].map(([l, n]) => (
                  <div key={l} className={`rounded-lg border p-3 ${n > 0 ? "border-amber-200 bg-amber-50" : "border-slate-100"}`}>
                    <div className={`text-xl font-bold ${n > 0 ? "text-amber-700" : "text-slate-300"}`}>{n}</div><div className="text-xs text-slate-500">{l}</div></div>
                ))}
              </div>
            )}
          </Card>
          <Card title="Verification Evidence">
            {data.verification_evidence.has_evidence ? (
              <div className="flex items-center gap-2 text-sm text-emerald-700"><ShieldCheck className="h-4 w-4" />Verified by {data.verification_evidence.verified_by} on {new Date(data.verification_evidence.verified_at).toLocaleString()}</div>
            ) : <div className="flex items-center gap-2 text-sm text-slate-500"><Clock className="h-4 w-4" />No genuine verification record yet — status reflects actual review state.</div>}
          </Card>
        </div>
      )}

      {tab === "Organization" && (
        <Card><div className="grid sm:grid-cols-2 gap-x-8">
          <Field label="Legal Company Name" value={co.legal_name} /><Field label="Display Name" value={co.display_name} />
          <Field label="Organization Type" value={co.organization_type} /><Field label="Industry" value={co.industry} />
          <Field label="Company Size" value={co.company_size} /><Field label="Year Established" value={co.year_established} />
          <Field label="Registered Address" value={co.registered_address} /><Field label="Headquarters" value={co.headquarters} />
          <Field label="City" value={co.city} /><Field label="State" value={co.state} /><Field label="Country" value={co.country} />
          <Field label="Website" value={co.website} /><Field label="Official Business Email" value={co.business_email} />
          <Field label="Corporate Phone" value={co.corporate_phone} />
          <Field label="PAN" value={co.pan} /><Field label="GSTIN" value={co.gst_registered ? co.gstin : null} pending={co.gst_registered} />
          <Field label="CIN" value={co.cin} /><Field label="Udyam / MSME" value={co.udyam} />
          <div className="sm:col-span-2"><Field label="Business Description" value={co.business_description} /></div>
        </div></Card>
      )}

      {tab === "Representative" && (
        <Card title="Authorized Representative">
          <div className="grid sm:grid-cols-2 gap-x-8">
            <Field label="Full Name" value={rep.name} /><Field label="Designation" value={rep.designation} />
            <Field label="Official Email" value={rep.email} /><Field label="Business Phone" value={rep.phone} />
            <Field label="Department" value={rep.department} /><Field label="LinkedIn" value={rep.linkedin} />
            <Field label="Authorization Declaration" value={rep.declaration} />
            <div><div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Verification</div><ChecklistBadge status={rep.verification_status === "verified" ? "verified" : "pending_review"} /></div>
          </div>
        </Card>
      )}

      {tab === "Leadership" && (
        <Card title="CEO / Leadership">
          <div className="grid sm:grid-cols-2 gap-x-8">
            <Field label="Name" value={lead.name} /><Field label="Leadership Type" value={lead.leadership_type} />
            <Field label="Designation" value={lead.designation} /><Field label="Official Email" value={lead.email} />
            <Field label="Business Contact" value={lead.phone} /><Field label="LinkedIn" value={lead.linkedin} />
            <div className="sm:col-span-2"><Field label="Leadership Bio" value={lead.bio} /></div>
          </div>
        </Card>
      )}

      {tab === "Business" && (
        <Card title="Business Details">
          {Object.keys(bd).length === 0 ? <p className="text-sm text-slate-400">No business details submitted yet.</p> : (
            <div className="grid sm:grid-cols-2 gap-x-8">{Object.entries(bd).map(([k, v]) => <Field key={k} label={k.replace(/_/g, " ")} value={v} />)}</div>
          )}
        </Card>
      )}

      {tab === "Documents" && (
        <Card title="Documents & KYC" className="!p-0">
          {documents.length === 0 ? <EmptyState icon="FileText" title="No documents uploaded" /> : (
            <div className="overflow-x-auto min-w-0"><table className="w-full min-w-[760px] text-sm" data-testid="documents-table">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100 bg-slate-50/50">
                <th className="px-4 py-3">Document</th><th className="px-4 py-3">Uploaded</th><th className="px-4 py-3">Verification</th><th className="px-4 py-3">Verified By</th><th className="px-4 py-3 text-right">Actions</th></tr></thead>
              <tbody>{documents.map((d) => (
                <tr key={d.id} className="border-b border-slate-50" data-testid={`document-row-${d.id}`}>
                  <td className="px-4 py-3"><div className="font-medium text-slate-800">{d.doc_type || d.name}</div><div className="text-xs text-slate-400">{d.original_filename}</div></td>
                  <td className="px-4 py-3 text-xs text-slate-500">{d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}{d.uploaded_by ? ` · ${d.uploaded_by}` : ""}</td>
                  <td className="px-4 py-3"><DocumentBadge status={d.verification_status || "pending"} />{d.rejection_reason ? <div className="text-xs text-rose-500 mt-0.5">{d.rejection_reason}</div> : null}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{d.verified_by || "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" className="text-emerald-600 border-emerald-200 mr-1" onClick={() => verifyDoc(d.id, "verified")} data-testid={`doc-verify-${d.id}`}>Verify</Button>
                    <Button size="sm" variant="outline" className="text-rose-600 border-rose-200" onClick={() => setReasonAction({ type: "doc-reject", docId: d.id })} data-testid={`doc-reject-${d.id}`}>Reject</Button>
                  </td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </Card>
      )}

      {tab === "Verification" && (
        <Card title="Verification Checklist">
          <div className="space-y-1.5" data-testid="verification-checklist">
            {checklist.map((c, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-50">
                <span className="text-sm text-slate-700">{c.label}{c.required ? <span className="text-rose-400 ml-1">*</span> : ""}</span>
                <ChecklistBadge status={c.status} />
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === "Requests" && (
        <Card title="Information & Document Requests" className="!p-0">
          {requests.length === 0 ? <EmptyState icon="MailQuestion" title="No requests sent" /> : (
            <div className="divide-y divide-slate-50" data-testid="requests-list">{requests.map((r) => (
              <div key={r.id} className="p-4">
                <div className="flex items-center justify-between"><span className="font-medium text-slate-800 capitalize">{r.request_type.replace(/_/g, " ")}</span><span className="text-xs font-semibold text-vs-royal">{r.status}</span></div>
                <div className="text-xs text-slate-400 mt-0.5">{r.requested_by_name || r.requested_by} · {new Date(r.created_at).toLocaleString()}</div>
                <div className="text-sm text-slate-600 mt-1">{r.requested_items.map((it) => it.label).join(", ")}</div>
              </div>
            ))}</div>
          )}
        </Card>
      )}

      {tab === "Activity" && (
        <Card title="Activity & Audit Timeline">
          {timeline.length === 0 ? <p className="text-sm text-slate-400">No recorded events.</p> : (
            <div className="space-y-3" data-testid="activity-timeline">{timeline.map((t, i) => (
              <div key={i} className="flex gap-3">
                <div className="h-2 w-2 rounded-full bg-vs-royal mt-1.5 shrink-0" />
                <div className="min-w-0"><div className="text-sm text-slate-700 capitalize">{(t.action || "").replace(/_/g, " ")}{t.prev_status || t.new_status ? <span className="text-slate-400"> {t.prev_status || "—"} → {t.new_status || "—"}</span> : ""}</div>
                  {t.detail ? <div className="text-xs text-slate-500">{t.detail}</div> : null}
                  <div className="text-xs text-slate-400">{t.actor_name || "System"} · {t.timestamp ? new Date(t.timestamp).toLocaleString() : ""}</div></div>
              </div>
            ))}</div>
          )}
        </Card>
      )}

      <RequestDialog open={!!reqDialog} onClose={() => setReqDialog(null)} orgId={orgId} data={data} kind={reqDialog} onDone={load} />
      <ReasonDialog open={reasonAction === "reject"} onClose={() => setReasonAction(null)} title="Reject Partner" label="Rejection reason"
        onConfirm={async (reason) => { await act(() => api.post(`/admin/onboarding/partners/${orgId}/reject`, { reason, category: "Other" }), "Partner rejected"); setReasonAction(null); }} />
      <ReasonDialog open={reasonAction === "suspend"} onClose={() => setReasonAction(null)} title="Suspend Partner" label="Suspension reason"
        onConfirm={async (reason) => { await act(() => api.post(`/admin/onboarding/partners/${orgId}/suspend`, { reason }), "Partner suspended"); setReasonAction(null); }} />
      <ReasonDialog open={reasonAction?.type === "doc-reject"} onClose={() => setReasonAction(null)} title="Reject Document" label="Rejection reason"
        onConfirm={async (reason) => { await verifyDoc(reasonAction.docId, "rejected", reason); setReasonAction(null); }} />
    </div>
  );
}
